import React from 'react';
import {
  Box, Typography, RadioGroup, FormControlLabel, Radio,
  TextField, Button, Snackbar, Alert, Paper, FormHelperText,
} from '@mui/material';
import DashboardLayout from '../../../shared/layout/DashboardLayout';
import { useParams } from 'react-router-dom';
import { useCuestionarioVentForm } from './hooks/useCuestionarioVentForm';

const FORM_COLORS = {
  primary: '#0d6aee',
  textMain: '#0f172a',
  textMuted: '#475569',
  border: '#dbe7f3',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#cbd5e1' },
    '&:hover fieldset': { borderColor: FORM_COLORS.primary },
    '&.Mui-focused fieldset': { borderColor: FORM_COLORS.primary, borderWidth: 2 },
  },
};

const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  mb: 3,
  borderRadius: 3,
  border: `1px solid ${FORM_COLORS.border}`,
  background: '#f8fbff',
  boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
};

const radioSx = { color: FORM_COLORS.primary, '&.Mui-checked': { color: FORM_COLORS.primary } };

const CuestionarioVent = () => {
  const { idPaciente, idCuestionario } = useParams();
  const {
    fumador, setFumador,
    cigarrosDia, setCigarrosDia,
    aniosDeshabituacion, setAniosDeshabituacion,
    ejercicio, setEjercicio,
    actividadFisica, setActividadFisica,
    pef, setPef,
    openGood, setOpenGood,
    openError, setOpenError,
    errors,
    guardarDatos,
  } = useCuestionarioVentForm({ idPaciente, idCuestionario });

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 3 }}>
        <Paper
          elevation={0}
          sx={{ width: '100%', maxWidth: 800, p: { xs: 2, md: 4 }, borderRadius: 4, border: `1px solid ${FORM_COLORS.border}` }}
        >
          <Typography variant="h4" sx={{ textAlign: 'center', color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
            Cuestionario VENT
          </Typography>
          <Typography sx={{ textAlign: 'center', color: FORM_COLORS.textMuted, mb: 4 }}>
            Valoración de hábitos tabáquicos, actividad física y función pulmonar.
          </Typography>

          {/* Tabaquismo */}
          <Paper elevation={0} sx={{ ...sectionPaperSx, ...(errors.fumador && { border: '1px solid #f87171' }) }}>
            <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
              1. ¿Es fumador?
            </Typography>
            <RadioGroup value={fumador} onChange={(e) => setFumador(e.target.value)}>
              <FormControlLabel value="si" control={<Radio sx={radioSx} />} label={<Typography sx={{ color: FORM_COLORS.textMain }}>Sí</Typography>} />
              <FormControlLabel value="no" control={<Radio sx={radioSx} />} label={<Typography sx={{ color: FORM_COLORS.textMain }}>No</Typography>} />
              <FormControlLabel value="lo-fue" control={<Radio sx={radioSx} />} label={<Typography sx={{ color: FORM_COLORS.textMain }}>Lo fue</Typography>} />
            </RadioGroup>
            {errors.fumador && <FormHelperText error>{errors.fumador}</FormHelperText>}
            {fumador === 'si' && (
              <TextField
                fullWidth
                label="Número de cigarrillos al día"
                type="number"
                value={cigarrosDia}
                onChange={(e) => setCigarrosDia(e.target.value)}
                sx={{ mt: 2, ...fieldSx }}
              />
            )}
            {fumador === 'lo-fue' && (
              <TextField
                fullWidth
                label="Años desde la deshabituación"
                type="number"
                value={aniosDeshabituacion}
                onChange={(e) => setAniosDeshabituacion(e.target.value)}
                sx={{ mt: 2, ...fieldSx }}
              />
            )}
          </Paper>

          {/* Actividad física */}
          <Paper elevation={0} sx={{ ...sectionPaperSx, ...(errors.ejercicio && { border: '1px solid #f87171' }) }}>
            <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 1 }}>
              2. ¿Realiza ejercicio físico?
            </Typography>
            <RadioGroup value={ejercicio} onChange={(e) => setEjercicio(e.target.value)}>
              <FormControlLabel value="si" control={<Radio sx={radioSx} />} label={<Typography sx={{ color: FORM_COLORS.textMain }}>Sí</Typography>} />
              <FormControlLabel value="no" control={<Radio sx={radioSx} />} label={<Typography sx={{ color: FORM_COLORS.textMain }}>No</Typography>} />
            </RadioGroup>
            {errors.ejercicio && <FormHelperText error>{errors.ejercicio}</FormHelperText>}
            {ejercicio === 'si' && (
              <TextField
                fullWidth
                label="Describa la actividad física"
                value={actividadFisica}
                onChange={(e) => setActividadFisica(e.target.value)}
                sx={{ mt: 2, ...fieldSx }}
              />
            )}
          </Paper>

          {/* Función pulmonar */}
          <Paper elevation={0} sx={{ ...sectionPaperSx, ...(errors.pef && { border: '1px solid #f87171' }) }}>
            <Typography variant="h6" sx={{ color: FORM_COLORS.textMain, fontWeight: 700, mb: 2 }}>
              3. Pruebas de ventilación
            </Typography>
            <TextField
              fullWidth
              required
              label="PEF (l/min)"
              type="number"
              value={pef}
              onChange={(e) => setPef(e.target.value)}
              error={!!errors.pef}
              helperText={errors.pef || 'Valor obligatorio para guardar el cuestionario'}
              inputProps={{ min: 0 }}
              sx={fieldSx}
            />
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={guardarDatos} sx={{ borderRadius: 2.5, px: 4 }}>
              Guardar respuestas
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar open={openGood} autoHideDuration={2000} onClose={() => setOpenGood(false)}>
        <Alert severity="success" onClose={() => setOpenGood(false)}>
          ¡Respuestas guardadas correctamente!
        </Alert>
      </Snackbar>
      <Snackbar open={openError} autoHideDuration={4000} onClose={() => setOpenError(false)}>
        <Alert severity="error" onClose={() => setOpenError(false)}>
          Revisa los campos obligatorios antes de guardar.
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default CuestionarioVent;
